import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "@/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { formatAZN, STATUS_LABELS } from "@/lib/format";
import { Users, Gavel, CheckCircle, XCircle, TrendingUp, CreditCard, Mail } from "lucide-react";

export default function AdminPage() {
  const { user, authHeaders } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "admin") { nav("/"); return; }
    const h = { headers: authHeaders() };
    axios.get(`${API}/admin/stats`, h).then(r => setStats(r.data));
    axios.get(`${API}/admin/users`, h).then(r => setUsers(r.data));
    axios.get(`${API}/admin/auctions`, h).then(r => setAuctions(r.data));
    axios.get(`${API}/admin/payments`, h).then(r => setPayments(r.data));
    axios.get(`${API}/admin/notifications`, h).then(r => setNotifs(r.data));
  }, [user]);

  const toggleUser = async (id) => {
    await axios.post(`${API}/admin/users/${id}/toggle-active`, {}, { headers: authHeaders() });
    axios.get(`${API}/admin/users`, { headers: authHeaders() }).then(r => setUsers(r.data));
    toast.success("Statusu dəyişdirildi");
  };
  const cancelAuction = async (id) => {
    await axios.post(`${API}/admin/auctions/${id}/cancel`, {}, { headers: authHeaders() });
    axios.get(`${API}/admin/auctions`, { headers: authHeaders() }).then(r => setAuctions(r.data));
    toast.success("Auksion ləğv edildi");
  };
  const deleteAuction = async (id) => {
    if (!window.confirm("Bu auksionu, bütün təkliflərini və ödənişlərini həmişəlik silmək istəyirsiniz?")) return;
    await axios.delete(`${API}/admin/auctions/${id}`, { headers: authHeaders() });
    axios.get(`${API}/admin/auctions`, { headers: authHeaders() }).then(r => setAuctions(r.data));
    toast.success("Auksion silindi");
  };
  const deleteAllDemo = async () => {
    if (!window.confirm("DİQQƏT: Bütün auksionları (və onların bütün təkliflərini + ödənişlərini) həmişəlik silmək istəyirsiniz? Bu geri qaytarıla bilməz.")) return;
    const { data } = await axios.post(`${API}/admin/auctions/bulk-delete`, { all_demo: true }, { headers: authHeaders() });
    axios.get(`${API}/admin/auctions`, { headers: authHeaders() }).then(r => setAuctions(r.data));
    toast.success(`${data.deleted} auksion silindi`);
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 fade-in">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">Admin panel</h1>
      {stats && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard icon={<Users />} label="İstifadəçi" value={stats.users} />
          <StatCard icon={<Gavel />} label="Aktiv" value={stats.active} />
          <StatCard icon={<CheckCircle />} label="Bitmiş" value={stats.ended} />
          <StatCard icon={<CheckCircle />} label="Satılıb" value={stats.sold} color="emerald" />
          <StatCard icon={<XCircle />} label="Satılmayıb" value={stats.unsold} color="red" />
          <StatCard icon={<TrendingUp />} label="Təkliflər" value={stats.bids} />
          <StatCard icon={<CreditCard />} label="Gəlir" value={formatAZN(stats.revenue)} color="blue" />
        </div>
      )}

      <Tabs defaultValue="users" className="mt-8">
        <TabsList className="rounded-full bg-slate-100 p-1 overflow-x-auto flex-wrap">
          <TabsTrigger value="users" className="rounded-full">İstifadəçilər</TabsTrigger>
          <TabsTrigger value="auctions" className="rounded-full">Auksionlar</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-full">Ödənişlər</TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-full">Emaillər</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <div className="rounded-2xl border border-slate-200 overflow-x-auto bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest">
                <tr><Th>Ad</Th><Th>Email</Th><Th>Telefon</Th><Th>Rol</Th><Th>Status</Th><Th></Th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map(u => (
                  <tr key={u.id}>
                    <Td>{u.first_name} {u.last_name}</Td>
                    <Td>{u.email}</Td>
                    <Td>{u.phone}</Td>
                    <Td><span className="px-2 py-0.5 rounded-md bg-slate-100 text-xs font-bold">{u.role}</span></Td>
                    <Td><span className={`px-2 py-0.5 rounded-md text-xs font-bold ${u.is_active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{u.is_active ? "Aktiv" : "Bloklu"}</span></Td>
                    <Td>{u.role !== "admin" && <Button size="sm" variant="outline" onClick={() => toggleUser(u.id)} className="rounded-full">{u.is_active ? "Blokla" : "Aktivləşdir"}</Button>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="auctions" className="mt-6">
          <div className="flex justify-end mb-3">
            <Button data-testid="admin-delete-all-btn" onClick={deleteAllDemo} variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-50 font-semibold">
              Bütün demo auksionları sil
            </Button>
          </div>
          <div className="rounded-2xl border border-slate-200 overflow-x-auto bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest"><tr><Th>Avtomobil</Th><Th>Status</Th><Th>Cari qiymət</Th><Th>Təkliflər</Th><Th></Th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {auctions.map(a => (
                  <tr key={a.id}>
                    <Td>{a.vehicle.brand} {a.vehicle.model} ({a.vehicle.year})</Td>
                    <Td><span className="px-2 py-0.5 rounded-md bg-slate-100 text-xs font-bold">{STATUS_LABELS[a.status]}</span></Td>
                    <Td className="font-mono font-bold">{formatAZN(a.current_price)}</Td>
                    <Td>{a.bid_count}</Td>
                    <Td className="flex gap-2 justify-end">
                      {["active","scheduled"].includes(a.status) && <Button size="sm" variant="outline" onClick={() => cancelAuction(a.id)} className="rounded-full">Ləğv et</Button>}
                      <Button data-testid={`admin-delete-${a.id}`} size="sm" variant="outline" onClick={() => deleteAuction(a.id)} className="rounded-full border-red-300 text-red-700 hover:bg-red-50">Sil</Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <div className="rounded-2xl border border-slate-200 overflow-x-auto bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest"><tr><Th>Auksion</Th><Th>Məbləğ</Th><Th>Status</Th><Th>Tarix</Th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {payments.map(p => (
                  <tr key={p.id}>
                    <Td className="font-mono text-xs">{p.auction_id.slice(0, 8)}</Td>
                    <Td className="font-mono font-bold">{formatAZN(p.amount)}</Td>
                    <Td><span className={`px-2 py-0.5 rounded-md text-xs font-bold ${p.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span></Td>
                    <Td>{new Date(p.created_at).toLocaleString("az-AZ")}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <div className="rounded-2xl border border-slate-200 overflow-x-auto bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-widest"><tr><Th>Alıcı</Th><Th>Mövzu</Th><Th>Status</Th><Th>Tarix</Th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {notifs.map(n => (
                  <tr key={n.id}>
                    <Td>{n.to}</Td><Td>{n.subject}</Td>
                    <Td><span className={`px-2 py-0.5 rounded-md text-xs font-bold ${n.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{n.status}</span></Td>
                    <Td>{new Date(n.created_at).toLocaleString("az-AZ")}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatCard = ({ icon, label, value, color = "slate" }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className={`h-9 w-9 rounded-lg bg-${color}-100 text-${color}-600 flex items-center justify-center mb-2`}>{React.cloneElement(icon, { className: "h-4 w-4" })}</div>
    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
    <div className="font-mono text-lg font-extrabold text-slate-900 mt-1">{value}</div>
  </div>
);
const Th = ({ children }) => <th className="text-left px-4 py-3 font-bold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
